import { useState, type ReactNode } from "react";
import { Button } from "react-bootstrap";

/*
    The shell the three delivery channels share: a label, an on/off pill, and a body that
    collapses.

    Rachel asked for this after seeing all three expanded at once. Mail, email and SMS each
    carry a full settings panel, and a campaign using none of them still pays for all three in
    vertical space, pushing Population and the rest of the page below the fold. She pointed at
    the Population section as the pattern she wanted, so this uses the same control and the
    same words rather than inventing a second idiom for the same gesture.

    **What stays visible when collapsed is the point.** The label and the on/off pill, because
    "is email on for this campaign" is the question somebody scanning the page is actually
    asking. Collapsing the answer away would trade one problem for a worse one.

    **Open when on, closed when off.** Rachel's words were "hide them while they're not in
    use", and a channel that is switched on is in use. Defaulting everything closed would hide
    a live channel's From address and sending window, which are the settings most worth seeing
    at a glance. Defaulting everything open is what she asked us to stop doing.

    The state is deliberately not persisted. It is a per-visit convenience, and a remembered
    collapse would eventually hide a channel from somebody who does not know it is there.

    **A collapsed channel still has to invite.** The first version of this collapsed to a small
    uppercase label and a "Show details" button, which reads as a technical setting somebody
    has already decided about rather than a thing this campaign could start doing. While a
    channel is off it now carries its icon, a line saying what it does, and a button that says
    what pressing it is for. On, it goes back to the quiet label: by then nobody needs the
    pitch.
*/

interface DeliveryChannelCardProps {
  /** What the channel is called on screen: "Direct Mail", "Email", "Text messages". */
  label: string;
  /** Whether the channel is switched on for this campaign. Also decides the initial state. */
  enabled: boolean;
  /** Shown above the body and outside the collapse, so a failure is never hidden. */
  error?: string | null;
  /**
   * One line saying what the channel does, shown while it is off.
   *
   * A row reading "DIRECT MAIL  off" is an accurate label and a useless invitation: it tells
   * somebody the name of a setting, not that this campaign could put a postcard in the post.
   * Once the channel is on the label is enough, because by then they know what it is.
   */
  description?: string;
  /** Bootstrap icon name, e.g. "bi-envelope". Gives the row something to aim at. */
  icon?: string;
  children: ReactNode;
}

export default function DeliveryChannelCard({
  label,
  enabled,
  error,
  description,
  icon,
  children,
}: DeliveryChannelCardProps) {
  const [open, setOpen] = useState(enabled);
  // While the channel is off, the button is the way in rather than a disclosure triangle, so
  // it says what it is for. Rachel asked for these collapsed when unused; she did not ask for
  // them to be hard to find, and "Show details" is the wording that made them feel technical.
  const invite = !enabled && !open;

  return (
    <>
      <div className="campaign-label d-flex align-items-center justify-content-between">
        <span className="d-flex align-items-center gap-2">
          {icon && <i className={`bi ${icon}`} style={{ color: "#5b3df5" }} />}
          <span>
            {label}
            <span className={`campaign-pill ${enabled ? "" : "campaign-pill--muted"}`}>
              {enabled ? "on" : "off"}
            </span>
            {invite && description && (
              <span
                className="d-block text-muted fw-normal mt-1"
                // `.campaign-label` uppercases its contents, which is right for a label and
                // wrong for a sentence: set in small caps it reads as shouting rather than
                // as an explanation.
                style={{ textTransform: "none", fontSize: 13, letterSpacing: 0 }}
              >
                {description}
              </span>
            )}
          </span>
        </span>
        <Button
          variant={invite ? "primary" : "outline-primary"}
          size="sm"
          onClick={() => setOpen((o) => !o)}
          className="d-flex align-items-center gap-1 flex-shrink-0"
          aria-expanded={open}
          aria-label={
            invite ? `Set up ${label}` : `${open ? "Hide" : "Show"} ${label} settings`
          }
        >
          <i className={`bi ${invite ? "bi-plus-lg" : open ? "bi-chevron-up" : "bi-chevron-down"}`} />
          {invite ? `Set up ${label.toLowerCase()}` : open ? "Hide details" : "Show details"}
        </Button>
      </div>

      {/* Outside the collapse on purpose. A save that failed while the panel was open must
          not disappear when somebody collapses it to get the page back. */}
      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      {open && <div className="campaign-card p-3">{children}</div>}
    </>
  );
}
